import { Fuel, ShoppingBag, Droplets, Clock, ExternalLink, Wrench, Gauge, Lightbulb } from "lucide-react";
import { MaskLine, Reveal, ClipImage } from "../components/Motion";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/api";

const AVIA_HERO = "https://customer-assets-wrfwihn1.emergentagent.net/job_autobedrijf-site/artifacts/sb52lni1_20200621_205751.webp";
const AVIA_URL = "https://www.aviavinkeveen.nl";

const POMP_ICONS = [Gauge, Droplets, Lightbulb, Fuel];
const POMP_DEFAULT = [
  { t: "Banden op spanning" },
  { t: "Motorvloeistoffen controleren" },
  { t: "Lampen vervangen" },
  { t: "AdBlue-tank vullen" },
];
const SHOP_DEFAULT = ["Rookwaren", "Zoetwaren", "Gekoelde dranken", "De krant (Telegraaf)", "Autolampen", "Motor- & versnellingsbakolie", "Koelvloeistof & antivries", "Ruitensproeiervloeistof", "AdBlue", "Jerrycans", "Poetsmiddelen", "Accu's", "Auto-elektra (zekeringen, startkabels)", "Wintermaterialen"];
const MENGING_DEFAULT = ["1 op 100", "1 op 50", "1 op 25", "1 op 20"];

export default function Avia() {
  const { get } = useContent();
  const pompServices = get("avia.pomp.items", POMP_DEFAULT);
  const shopItems = get("avia.shop.items", SHOP_DEFAULT);
  const menging = get("avia.mengsmering.ratios", MENGING_DEFAULT);
  return (
    <div className="pb-24 min-h-screen">
      {/* HERO */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary mb-4 flex items-center gap-2">
              <Fuel size={15} /> {get("avia.hero.eyebrow", "Onderdeel van Automobielbedrijf Stoof")}
            </p>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-text-main">
              <MaskLine>{get("avia.hero.title1", "AVIA")}</MaskLine>
              <MaskLine delay={0.12}><span className="text-primary">{get("avia.hero.title2", "Vinkeveen")}</span></MaskLine>
            </h1>
            <p className="mt-6 text-lg text-text-muted max-w-md leading-relaxed">
              {get("avia.hero.intro", "Ons bediende AVIA-tankstation aan de Herenweg. U tanken, wij aftanken — plus een shop en volop service aan uw auto. Servicegericht, net zoals u van ons gewend bent.")}
            </p>
            <a
              href={AVIA_URL} target="_blank" rel="noreferrer" data-testid="avia-website-link"
              className="mt-8 group inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-4 hover:bg-primary-light transition-colors"
            >
              {get("avia.hero.cta", "Bezoek aviavinkeveen.nl")}
              <ExternalLink size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
          <ClipImage src={fileUrl(get("afbeeldingen.avia_hero", AVIA_HERO))} alt="AVIA tankstation Vinkeveen" className="aspect-[4/3]" />
        </div>
      </section>

      {/* OPENINGSTIJDEN */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
          <Reveal className="bg-background p-8">
            <p className="text-xs font-bold uppercase tracking-wide text-primary flex items-center gap-2"><Clock size={15} /> {get("avia.open.service_label", "Bediende service")}</p>
            <div className="mt-3 text-text-main font-display font-bold text-xl">{get("avia.open.service_l1", "Ma t/m vr: 07:00 – 19:30")}</div>
            <div className="text-text-main font-display font-bold text-xl">{get("avia.open.service_l2", "Zaterdag: 08:00 – 18:00")}</div>
            <p className="text-text-muted text-sm mt-2">{get("avia.open.service_note", "Wij tanken uw auto af — u hoeft zelf niets te doen.")}</p>
          </Reveal>
          <Reveal delay={0.08} className="bg-background p-8">
            <p className="text-xs font-bold uppercase tracking-wide text-primary flex items-center gap-2"><Fuel size={15} /> {get("avia.open.automat_label", "Betaalautomaat")}</p>
            <div className="mt-3 text-text-main font-display font-bold text-xl">{get("avia.open.automat_l1", "7 dagen per week")}</div>
            <div className="text-text-main font-display font-bold text-xl">{get("avia.open.automat_l2", "06:00 – 23:00")}</div>
            <p className="text-text-muted text-sm mt-2">{get("avia.open.automat_note", "Buiten de bediende uren tankt u eenvoudig zelf bij de automaat.")}</p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE AAN DE POMP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <Reveal>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl">{get("avia.pomp.title", "Service aan de pomp")}</h2>
          <p className="text-text-muted mt-3 max-w-xl">{get("avia.pomp.intro", "Naast tanken voeren we graag extra werkzaamheden voor u uit:")}</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {pompServices.map((s, i) => {
            const Icon = POMP_ICONS[i] || Gauge;
            return (
              <Reveal key={i} delay={(i % 4) * 0.06} className="bg-background p-8">
                <Icon size={26} className="text-primary" strokeWidth={1.6} />
                <h3 className="font-display font-bold text-lg mt-4">{s.t}</h3>
              </Reveal>
            );
          })}
        </div>
        <p className="text-text-muted text-sm mt-6 max-w-2xl leading-relaxed flex items-start gap-2">
          <Wrench size={16} className="text-primary mt-0.5 shrink-0" />
          {get("avia.pomp.note", "In samenwerking met onze werkplaats verzorgen we ook bandenreparatie, foutcodes uitlezen, reset van bandenspanningssystemen, vervangen van xenonlampen en meer.")}
        </p>
      </section>

      {/* MENGSMERING + SHOP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid lg:grid-cols-2 gap-8">
        <Reveal className="bg-primary text-white p-8 sm:p-10 grain relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-display font-bold text-2xl sm:text-3xl">{get("avia.mengsmering.title", "Mengsmering")}</h2>
            <p className="mt-4 text-white/80 leading-relaxed text-sm">
              {get("avia.mengsmering.text", "Voor tweetaktmotoren (buitenboordmotoren, kettingzagen, brommers, bosmaaiers) tanken wij de juiste mengverhouding met onze verstelbare mengsmeringspomp:")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {menging.map((m, i) => (
                <span key={i} className="rounded-full bg-white/10 border border-white/20 px-4 py-2 font-display font-bold">{m}</span>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.08} className="border border-border bg-surface p-8 sm:p-10">
          <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3"><ShoppingBag size={24} className="text-primary" /> {get("avia.shop.title", "In onze shop")}</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {shopItems.map((item, i) => (
              <span key={i} className="rounded-full bg-background border border-border px-3 py-1.5 text-sm text-text-main">{item}</span>
            ))}
          </div>
          <p className="text-text-muted text-sm mt-6">{get("avia.shop.note", "Onze shopmedewerker adviseert u graag. Montage en het bijvullen van vloeistoffen kan direct in onze werkplaats.")}</p>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12">
        <Reveal>
          <div className="relative overflow-hidden bg-primary text-white grain px-8 sm:px-16 py-16 text-center">
            <div className="relative z-10">
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight uppercase">{get("avia.cta.title", "Meer weten over AVIA Vinkeveen?")}</h2>
              <a href={AVIA_URL} target="_blank" rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent text-primary font-bold px-8 py-4 hover:bg-white transition-colors group">
                {get("avia.cta.btn", "Ga naar aviavinkeveen.nl")}
                <ExternalLink size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
